// ============================================================================
// ClientsPage — route /app/clients  (namespace: "clients")
// ----------------------------------------------------------------------------
// The full client roster: search, filter (all / favorites / follow-up /
// archived), star/favourites, status + last-session recency per card, and the
// "new client" entry point. Each card taps through to /app/clients/:id.
//
// Data flows through the typed React Query hooks:
//   • useClients()  — GET /api/clients
//   • useCalendar() — GET /api/calendar (trainer-wide; bucketed client-side in
//                     lib.ts to derive each client's last / next session)
//   • useCreateClient() / useStarClient() — both optimistic + live-synced
// The AppShell's live-sync socket invalidates qk.clients.* / qk.sessions.* on a
// change from another device, so the roster stays fresh with no reload.
//
// Optional-first throughout: creating a client needs only a name. Mobile-first
// (single column → grid from sm:); rich, reduced-motion-aware motion.
//
// Routing + i18n are pre-wired (see app/router.tsx, i18n/config.ts); this file
// stays a DEFAULT export named ClientsPage and owns the "clients" namespace.
// ============================================================================
import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { Badge, Button, EmptyState, Tabs } from "../../components";
import { IconPlus, IconUsers, IconStar } from "../../components/icons";
import { useClients } from "../../hooks/clients";
import { useCalendar } from "../../hooks/sessions";
import { useRosterUnread } from "../../hooks/messages";
import { LoadingBlock, ErrorBlock } from "../../app/PageState";
import { cn } from "../../lib/cn";
import { ClientCard } from "./ClientCard";
import { CreateClientDrawer } from "./CreateClientDrawer";
import { InactiveClientsSection } from "../../dashboard/InactiveClients";
import {
  buildActivity,
  countByFilter,
  selectRoster,
  type ClientFilter,
} from "./lib";

export default function ClientsPage() {
  const { t } = useTranslation("clients");
  const { t: tm } = useTranslation("messages");
  const reduce = useReducedMotion();

  const clientsQuery = useClients();
  const calendarQuery = useCalendar();
  // Unread (client→trainer) messages across all threads — the roster badges +
  // the visible grand total. Kept fresh by the `message` live-sync invalidation.
  const unreadQuery = useRosterUnread();

  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<ClientFilter>("all");
  const [createOpen, setCreateOpen] = useState(false);

  // Deep-link support: the dashboard "gone quiet" card links here with
  // ?filter=followUp so a trainer lands on the follow-up lens. Consumed once.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const f = searchParams.get("filter");
    const known: ClientFilter[] = ["all", "favorites", "followUp", "archived"];
    if (f && (known as string[]).includes(f)) {
      setFilter(f as ClientFilter);
      searchParams.delete("filter");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const clients = useMemo(() => clientsQuery.data ?? [], [clientsQuery.data]);
  const sessions = useMemo(
    () => calendarQuery.data ?? [],
    [calendarQuery.data],
  );
  const unreadByClient = unreadQuery.data?.by_client ?? {};
  const unreadTotal = unreadQuery.data?.total ?? 0;

  const activity = useMemo(
    () => buildActivity(clients, sessions),
    [clients, sessions],
  );
  const counts = useMemo(
    () => countByFilter(clients, activity),
    [clients, activity],
  );
  const rows = useMemo(
    () => selectRoster(clients, activity, filter, query),
    [clients, activity, filter, query],
  );

  const hasAnyClients = clients.length > 0;
  // Calendar is supplementary (recency only) — never block the roster on it.
  const loading = clientsQuery.isLoading;
  const errored = clientsQuery.isError;

  const fade = (delay = 0) => ({
    initial: reduce ? false : { opacity: 0, y: 14 },
    animate: { opacity: 1, y: 0 },
    transition: {
      duration: 0.4,
      delay: reduce ? 0 : delay,
      ease: [0.22, 0.61, 0.36, 1] as const,
    },
  });

  const filterTabs = [
    { id: "all", label: t("filters.all"), badge: <Count n={counts.all} active={filter === "all"} /> },
    {
      id: "favorites",
      label: t("filters.favorites"),
      badge: <Count n={counts.favorites} active={filter === "favorites"} />,
    },
    {
      id: "followUp",
      label: t("filters.followUp"),
      badge: <Count n={counts.followUp} active={filter === "followUp"} />,
    },
    {
      id: "archived",
      label: t("filters.archived"),
      badge: <Count n={counts.archived} active={filter === "archived"} />,
    },
  ];

  return (
    <div className="space-y-6">
      {/* Page header */}
      <motion.div
        {...fade(0)}
        className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between"
      >
        <div>
          <p className="text-sm font-bold uppercase tracking-kicker text-coral">
            {t("kicker")}
          </p>
          <h1 className="mt-1 font-display text-3xl font-extrabold tracking-tightest text-body sm:text-4xl">
            {t("title")}
          </h1>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <p className="text-body-soft">
              {t("subtitle", { count: counts.all })}
            </p>
            {unreadTotal > 0 && (
              <span
                className="inline-flex items-center gap-1.5 rounded-pill bg-coral-soft px-2.5 py-0.5 text-xs font-extrabold text-coral-600 ring-1 ring-inset ring-coral/15"
                aria-label={tm("unreadTotalAria", { count: unreadTotal })}
              >
                <span className="grid h-4 min-w-[1rem] place-items-center rounded-pill bg-coral px-1 text-[0.62rem] tabular-nums text-white">
                  {unreadTotal}
                </span>
                {tm("unreadTotal")}
              </span>
            )}
          </div>
        </div>
        <Button
          className="self-start sm:self-auto"
          iconLeft={<IconPlus width={18} height={18} />}
          onClick={() => setCreateOpen(true)}
        >
          {t("actions.newClient")}
        </Button>
      </motion.div>

      {errored ? (
        <ErrorBlock onRetry={() => void clientsQuery.refetch()} />
      ) : loading ? (
        <LoadingBlock label={t("loading")} />
      ) : !hasAnyClients ? (
        // First-run: no clients at all.
        <motion.div {...fade(0.04)}>
          <EmptyState
            title={t("empty.firstTitle")}
            description={t("empty.firstBlurb")}
            action={
              <Button
                iconLeft={<IconPlus width={18} height={18} />}
                onClick={() => setCreateOpen(true)}
              >
                {t("actions.newClient")}
              </Button>
            }
          />
        </motion.div>
      ) : (
        <>
          {/* Search + filters */}
          <motion.div {...fade(0.04)} className="space-y-4">
            <SearchBar
              value={query}
              onChange={setQuery}
              placeholder={t("search.placeholder")}
              clearLabel={t("search.clear")}
            />
            <div className="-mx-1 overflow-x-auto px-1 pb-1">
              <Tabs
                value={filter}
                onChange={(id) => setFilter(id as ClientFilter)}
                items={filterTabs}
              />
            </div>
          </motion.div>

          {/* Wave 6: backend-driven "gone quiet" surface (last_active_at past the
              inactivity window) — distinct from the session-recency followUp
              filter. Shown on the overview lenses, hidden while searching so the
              results stay focused. */}
          {!query && (filter === "all" || filter === "followUp") && (
            <motion.div {...fade(0.05)}>
              <InactiveClientsSection />
            </motion.div>
          )}

          {/* Roster */}
          {rows.length === 0 ? (
            <motion.div {...fade(0.06)}>
              <EmptyState
                icon={<NoMatchArt filter={filter} />}
                title={query ? t("empty.noMatchTitle") : t(`empty.${filter}Title`)}
                description={
                  query
                    ? t("empty.noMatchBlurb", { query })
                    : t(`empty.${filter}Blurb`)
                }
                action={
                  query ? (
                    <Button variant="ghost" onClick={() => setQuery("")}>
                      {t("search.clear")}
                    </Button>
                  ) : filter === "all" ? (
                    <Button
                      iconLeft={<IconPlus width={18} height={18} />}
                      onClick={() => setCreateOpen(true)}
                    >
                      {t("actions.newClient")}
                    </Button>
                  ) : undefined
                }
              />
            </motion.div>
          ) : (
            <motion.div {...fade(0.06)}>
              <div className="mb-3 flex items-center justify-between">
                <p className="text-sm font-medium text-body-soft">
                  {t("resultCount", { count: rows.length })}
                </p>
                {calendarQuery.isError && (
                  <Badge tone="warning">{t("recency.unavailable")}</Badge>
                )}
              </div>
              <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
                <AnimatePresence mode="popLayout" initial={false}>
                  {rows.map((row, i) => (
                    <ClientCard
                      key={row.client.id}
                      client={row.client}
                      activity={row.activity}
                      unread={unreadByClient[String(row.client.id)] ?? 0}
                      index={i}
                    />
                  ))}
                </AnimatePresence>
              </ul>
            </motion.div>
          )}
        </>
      )}

      <CreateClientDrawer
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={() => {
          // The roster refreshes from the invalidated query; the new client
          // surfaces here. We keep the trainer on the list (clearing any active
          // filter/search) so the just-added client is visible to tap through.
          setFilter("all");
          setQuery("");
        }}
      />
    </div>
  );
}

// --- search input -----------------------------------------------------------

function SearchBar({
  value,
  onChange,
  placeholder,
  clearLabel,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  clearLabel: string;
}) {
  return (
    <div className="relative">
      <span className="pointer-events-none absolute inset-y-0 left-4 flex items-center text-body-faint">
        <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" aria-hidden>
          <circle cx="11" cy="11" r="6.5" stroke="currentColor" strokeWidth="2" />
          <path d="m16 16 4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
      </span>
      <input
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        aria-label={placeholder}
        className={cn(
          "h-12 w-full rounded-pill bg-surface pl-11 pr-11 text-base text-body placeholder:text-body-faint",
          "ring-1 ring-inset ring-line-strong shadow-xs transition-shadow duration-fast",
          "focus:ring-2 focus:ring-inset focus:ring-volt focus-visible:outline-none",
          // Hide the native clear affordance — we render our own.
          "[&::-webkit-search-cancel-button]:appearance-none",
        )}
      />
      {value && (
        <button
          type="button"
          onClick={() => onChange("")}
          aria-label={clearLabel}
          className="absolute inset-y-0 right-3 my-auto grid h-8 w-8 place-items-center rounded-pill text-body-muted transition-colors hover:bg-surface-sunken hover:text-body"
        >
          <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" aria-hidden>
            <path d="M6 6l12 12M18 6 6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>
      )}
    </div>
  );
}

function Count({ n, active }: { n: number; active: boolean }) {
  return (
    <span
      className={cn(
        "grid h-5 min-w-[1.25rem] place-items-center rounded-pill px-1.5 text-[0.68rem] font-extrabold tabular-nums",
        active ? "bg-white/25 text-white" : "bg-surface text-body-soft ring-1 ring-line",
      )}
    >
      {n}
    </span>
  );
}

/** Small icon for the per-filter empty states. */
function NoMatchArt({ filter }: { filter: ClientFilter }) {
  const Icon = filter === "favorites" ? IconStar : IconUsers;
  return (
    <span className="grid h-20 w-20 place-items-center rounded-full bg-coral-soft text-coral">
      <Icon width={36} height={36} />
    </span>
  );
}
